import { useMemo } from "react";
import axios from "axios";
import { useQueryParams } from "src/reusable/Chat/hooks";
import { ChatConfig } from "src/reusable/Chat/types";
import { useGetConfigBySlugName } from "../AssistantServices";
import { PersonaData } from "../stores/ChatStore";
import { AtomAssistQueryParams } from "../types";
import useGetConfigFromDialogueHistory from "./useGetConfigFromDialogueHistory";

const useResolvePersona = ({
  initialConfig,
  initialUserMessage,
  allowUrlSync = false,
  ignoreDialogueHistory = false,
}: {
  initialConfig?: ChatConfig;
  initialUserMessage?: string;
  allowUrlSync?: boolean;
  ignoreDialogueHistory?: boolean;
} = {}) => {
  const [getQueryParam, setQueryParams] =
    useQueryParams<AtomAssistQueryParams>();

  // URL persona handling (only when allowUrlSync is true)
  const personaSlug = allowUrlSync ? getQueryParam("persona") : null;

  // Fetch persona config if URL has persona parameter (only when allowUrlSync is true)
  const {
    data: urlConfigData,
    isSuccess: urlConfigDataSuccess,
    isLoading: urlConfigDataLoading,
    isError: urlConfigDataError,
    error: urlConfigError,
  } = useGetConfigBySlugName(personaSlug || null);

  // The slug in the URL names an agent this user cannot see: removed, or
  // private and not shared with them (the backend answers both with 404).
  const personaNotFound = Boolean(
    allowUrlSync &&
    personaSlug &&
    urlConfigDataError &&
    axios.isAxiosError(urlConfigError) &&
    urlConfigError.response?.status === 404,
  );

  const {
    chatConfig: configFromDialogue,
    isLoading: isConfigDialogueLoading,
    isError: isConfigDialogueError,
    error: configDialogueError,
  } = useGetConfigFromDialogueHistory({
    // Always allow dialogue history loading when not providing initial config
    initialUserMessage: initialConfig ? initialUserMessage : undefined,
  });

  // Simple persona resolution - just return the resolved persona or null
  // When null, the backend will use GeneralLLM from resource slot bindings
  const resolvedPersona: PersonaData | null = useMemo(() => {
    // Base config with null model - backend will resolve from GeneralLLM slot
    const baseConfig = {
      modelId: null as number | null,
      modelVersionNum: null as number | null,
      datasets: [],
    };

    // Priority order: 1) initialConfig, 2) URL persona (if allowed), 3) dialogue config, 4) null (base config)
    switch (true) {
      // Initial config provided directly (highest priority)
      case Boolean(
        initialConfig?.name &&
        initialConfig?.slug &&
        initialConfig?.chatConfigId,
      ):
        return {
          id: initialConfig!.chatConfigId!,
          slug: initialConfig!.slug ?? "",
          name: initialConfig!.name ?? "",
          description: initialConfig!.description || "",
          initialPrompt: initialConfig!.initialPrompt || "",
          systemPrompt: initialConfig!.systemPrompt || "",
          datasets: initialConfig!.datasets || [],
          config: {
            modelId: initialConfig!.modelId,
            modelVersionNum: initialConfig!.modelVersionNum,
            chatConfigId: initialConfig!.chatConfigId,
            initialPrompt: initialConfig!.initialPrompt || "",
            systemPrompt: initialConfig!.systemPrompt || "",
            datasets: initialConfig!.datasets || [],
            chatControlMetadata: initialConfig!.chatControlMetadata,
            topSearchResults: initialConfig!.topSearchResults,
            toolCalling: initialConfig!.toolCalling,
            modelCapabilities: initialConfig!.modelCapabilities,
          },
          modelCapabilities: initialConfig!.modelCapabilities,
        };

      case Boolean(
        allowUrlSync &&
        personaSlug &&
        urlConfigDataSuccess &&
        urlConfigData &&
        urlConfigData.chatConfigId,
      ):
        // URL persona (only if allowUrlSync is true and data is loaded)
        // Config carries operational fields only; identity stays in PersonaData
        return {
          id: urlConfigData!.chatConfigId!,
          slug: urlConfigData!.slug || "",
          name: urlConfigData!.name || "",
          description: urlConfigData!.description || "",
          initialPrompt: urlConfigData!.initialPrompt || "",
          systemPrompt: urlConfigData!.systemPrompt || "",
          datasets: urlConfigData!.datasets || [],
          config: {
            modelId: urlConfigData!.modelId ?? null,
            modelVersionNum: urlConfigData!.modelVersionNum ?? null,
            chatConfigId: urlConfigData!.chatConfigId,
            initialPrompt: urlConfigData!.initialPrompt || "",
            systemPrompt: urlConfigData!.systemPrompt || "",
            datasets: urlConfigData!.datasets || [],
            topSearchResults: urlConfigData!.topSearchResults,
            toolCalling: urlConfigData!.toolCalling,
            modelCapabilities: urlConfigData!.modelCapabilities,
          },
          modelCapabilities: urlConfigData!.modelCapabilities,
        };

      case Boolean(
        !ignoreDialogueHistory &&
        configFromDialogue &&
        Object.keys(configFromDialogue).length > 0 &&
        configFromDialogue.name &&
        configFromDialogue.slug &&
        configFromDialogue.chatConfigId,
      ):
        // Config from dialogue history — operational fields only in config
        return {
          id: configFromDialogue!.chatConfigId!,
          slug: configFromDialogue!.slug ?? "",
          name: configFromDialogue!.name ?? "",
          description: configFromDialogue!.description || "",
          initialPrompt: configFromDialogue!.initialPrompt || "",
          systemPrompt: configFromDialogue!.systemPrompt || "",
          datasets: configFromDialogue!.datasets || [],
          config: {
            ...baseConfig,
            chatConfigId: configFromDialogue!.chatConfigId,
            initialPrompt: configFromDialogue!.initialPrompt || "",
            systemPrompt: configFromDialogue!.systemPrompt || "",
            datasets: configFromDialogue!.datasets || [],
            topSearchResults: configFromDialogue!.topSearchResults,
            toolCalling: configFromDialogue!.toolCalling,
            modelCapabilities: configFromDialogue!.modelCapabilities,
          },
          modelCapabilities: configFromDialogue!.modelCapabilities,
        };

      default:
        // If no persona sources, resolvedPersona stays null
        // Backend will use GeneralLLM from resource slot bindings
        return null;
    }
  }, [
    initialConfig,
    allowUrlSync,
    personaSlug,
    urlConfigDataSuccess,
    urlConfigData,
    configFromDialogue,
    ignoreDialogueHistory,
  ]);

  // Return helper functions for URL manipulation (only when allowUrlSync is true)
  const updateUrlPersona = (slug: string | null) => {
    if (allowUrlSync) {
      setQueryParams({ persona: slug });
    }
  };

  const isLoading =
    isConfigDialogueLoading || (allowUrlSync ? urlConfigDataLoading : false);
  const isError = isConfigDialogueError;
  const isSuccess = true; // No longer depends on wordConfig

  return {
    resolvedPersona, // null is base case - backend handles model resolution
    isLoading,
    isError,
    isSuccess,
    updateUrlPersona,
    hasUrlPersona: Boolean(personaSlug),
    personaNotFound,
    errors: {
      configDialogueError,
    },
  };
};

export default useResolvePersona;
